import React, {useState, useEffect, useRef} from "react";

import Chart from "chart.js/auto";
import 'chartjs-adapter-moment';
import Slider from 'react-input-slider';

import '../../assets/styles/App.css';
import "@fontsource/montserrat";



export default function CardPredictionDashboard(props) {
  const canvas = useRef(null);
  const [chartData, setChartData] = useState({});
  const [chartNewData, setChartNewData] = useState(props.chartData?.[props.chartData.days_past_due_collected_index]);
  const [yieldChart, setYieldChart] = useState({});
  const [slider, setSlider] = useState({x:props.chartData.days_past_due_collected_index});
  const minValue = Math.min(...props.chartData.total_index);
  const maxValue = Math.max(...props.chartData.total_index);
  const screenSize = (window.innerWidth >= 768);

  useEffect(() => {
      const config = {
          data: {
            labels: ['Term Due', 'Initial Due', 'Extended Due', '14 Days Past Due', '30 Days Past Due'],
            datasets: [{
                type: 'line',
                label: 'Current yield',
                backgroundColor: 'rgba(0, 116, 222, 1)',
                borderColor: 'rgba(0, 116, 222, 1)',
                data: props.chartData.yield_current,
                fill: false,
                barThickness: 4,
              }, {
                type: 'line',
                label: 'Expected Yield',
                backgroundColor: 'rgba(255, 230, 1, 0.8)',
                borderColor: 'rgba(255, 230, 1, 0.8)',
                data: chartNewData.yield_new,
                fill: false,
                barThickness: 4,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  padding: 30,
                  color: 'white',
                  pointStyle: 'circle',
                  usePointStyle: true,
                  }
              },
              tooltip: {
                mode: "index",
                intersect: false,
              },
              title: {
                display: false,
                text: "Orders Chart",
              },
            },
            elements: {
              point:{
                radius: 0
              }
            },
            hover: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x:
                {
                  display: true,
                  title: {
                    display: true,
                    labelString: "Days Past Due",
                  },
                  gridLines: {
                    display: false,
                    color: "rgba(0, 0, 0, 0)",
                    zeroLineColor: "rgba(0, 0, 0, 0)",
                  },
                  ticks: {
                    color: 'white',
                    autoSkip: true,
                    maxTicksLimit: 5,
                  },
                },
              y: {
                  min: 0.45,
                  type: 'linear',
                  position: 'left',
                  display: true,
                  title: {
                    display: true,
                    color: 'white',
                    labelString: "Yield",
                  },
                  gridLines: {
                    display: false,
                    drawBorder: false,
                    color: "white",
                    zeroLineColor: "rgba(100, 100, 100, 0.1)",
                    zeroLineWidth: 1
                  },
                  ticks: {
                    color: 'white',
                    autoSkip: true,
                    maxTicksLimit: 7,
                    max: 110,
                    min: 40,
                    callback: function(value, index, values) {
                        return value * 100 + '%';
                    }
                  },
                },
            },
          },
        };

        const chart = new Chart(canvas.current, config);
        setYieldChart(chart);

        return () => {
          // need cleanup because React runs useEffect callback twice in dev env
          chart.destroy();
        };

  }, [props.chartData]);

  const update_data = (number) => {
      setChartNewData(props.chartData?.[number]);
      addData(yieldChart, props.chartData?.[number]);
  };

  function addData(chart, newData) {
    chart.data.datasets[1].data = newData.yield_new;
    chart.update();
}

  return (
      <>
          <div className="relative container mx-auto mb-2 mt-8">

              <div className="relative w-full px-4 mb-2">
                  <div className="relative bg-white-2 w-full rounded-lg p-4 mb-2">

                      <p className="text-yellow text-20-30 mb-4">Set your risk appetite</p>
                      <div className="mb-4">
                          <p className="pb-4 text-14-400 text-white">
                              <b>Lower</b>
                              <span className="text-right text-14-600">Higher</span>
                          </p>

                          <Slider
                              styles={{
                                  track: {
                                      backgroundColor: '#ffffff',
                                      height: 10,
                                      width: '100%',
                                  },
                                  active: {
                                      backgroundColor: 'rgba(0, 116, 222, 1)'
                                  },
                                  thumb: {
                                      backgroundColor: 'rgba(0, 116, 222, 1)',
                                      width: 30,
                                      height: 30
                                  },
                              }}
                              xmin={minValue}
                              xmax={maxValue}
                              axis="x"
                              xstep={1}
                              x={slider.x}
                              onChange={({x}) => { setSlider(slider => ({...slider, x})); update_data(x); }}
                          />
                      </div>


                  </div>
              </div>

              <div className="flex flex-wrap">

                  <div className="relative md:w-6/12 lg:w-6/12 px-4 mb-2">
                      <div className="relative bg-white-2 w-full rounded-lg p-4">

                          <div className="w-full">
                              <p className="text-yellow text-16-19 mb-4">Current Disbursements</p>
                              <p className="text-white hl-35">
                                  {new Intl.NumberFormat().format(props.chartData.disbursement_current[0])}
                              </p>
                              <span className="text-right text-white text-14-600">
                                  Cumulative Disbursed Amount in { props.chartData.currency }
                              </span>
                          </div>


                      </div>
                  </div>

                  <div className="relative md:w-6/12 lg:w-6/12 px-4 mb-2">
                      <div className="relative bg-white-2 w-full rounded-lg p-4">

                          <div className="w-full">
                              <p className="text-yellow text-16-19 mb-4">Expected Disbursements</p>
                              <p className="text-white hl-35">
                                  {new Intl.NumberFormat().format(chartNewData.disbursement_new[0])}
                              </p>
                              <span className="text-right text-white text-14-600">
                                  Change: {new Intl.NumberFormat().format(chartNewData.disbursement_new[0] - props.chartData.disbursement_current[0])}
                              </span>
                          </div>


                      </div>
                  </div>


                  <div className="relative md:w-6/12 lg:w-6/12 px-4 mb-2">
                      <div className="relative bg-white-2 w-full rounded-lg p-4">

                          <div className="w-full">
                              <p className="text-yellow text-16-19 mb-4">Current Yield</p>
                              <p className="text-white hl-35">
                                  {new Intl.NumberFormat().format(props.chartData.yield_current[4] * 100)}%
                              </p>
                              <span className="text-right text-white text-14-600">
                                  Cumulative Yield at 30 Days past Due
                              </span>
                          </div>


                      </div>
                  </div>

                  <div className="relative md:w-6/12 lg:w-6/12 px-4 mb-2">
                      <div className="relative bg-white-2 w-full rounded-lg p-4">

                          <div className="w-full">
                              <p className="text-yellow text-16-19 mb-4">Expected Yield</p>
                              <p className="text-white hl-35">
                                  {new Intl.NumberFormat().format(chartNewData.yield_new[4] * 100 )}%
                              </p>
                              <span className="text-right text-white text-14-600">
                                  Change: {new Intl.NumberFormat().format((chartNewData.yield_new[4] - props.chartData.yield_current[4])*100)}%
                              </span>
                          </div>


                      </div>
                  </div>

              </div>

              <div className="relative w-full px-4 mb-8">
                  <div className="relative bg-white-2 w-full rounded-lg p-4 mb-8">

                      <div className="w-full">
                          <p className={`text-yellow text-20-30 mb-2 ${screenSize && 'px-8'}`}>Expected Yield Curve</p>
                          <div className="relative h-400-px">
                              <canvas ref={canvas}></canvas>
                          </div>
                      </div>

                  </div>
              </div>

          </div>

      </>
  );
}
